/**
 * OpenWorldDriveHUD - transparentes HUD fuer freie Fahrt
 */

import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import type { PlayerStatus, WeatherState } from '../engine/OpenWorldManager';
import { BW_CITIES } from '../engine/OpenWorldMapLoader';

interface OpenWorldDriveHUDProps {
  playerStatus: PlayerStatus | null;
  weather: WeatherState | null;
  timeOfDay: string | null;
  headingRad: number;
}

export const OpenWorldDriveHUD: React.FC<OpenWorldDriveHUDProps> = ({
  playerStatus,
  weather,
  timeOfDay,
  headingRad
}) => {
  const minimapRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!playerStatus || !minimapRef.current) return;

    const canvas = minimapRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = canvas.width;
    const scale = 8000; // Pixel pro Grad

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = 'rgba(12, 18, 26, 0.85)';
    ctx.fillRect(0, 0, size, size);

    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 10; i += 1) {
      ctx.beginPath();
      ctx.moveTo(i * 15, 0);
      ctx.lineTo(i * 15, size);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * 15);
      ctx.lineTo(size, i * 15);
      ctx.stroke();
    }

    const mapRadius = 260;
    const metersToPixels = size / (mapRadius * 2);
    const playerPos = playerStatus.position;

    const worldToMap = (x: number, z: number) => ({
      x: size / 2 + (x - playerPos.x) * metersToPixels,
      y: size / 2 - (z - playerPos.z) * metersToPixels
    });

    if (playerStatus.nearbyRoads && playerStatus.nearbyRoads.length > 0) {
      ctx.strokeStyle = 'rgba(120,200,255,0.45)';
      ctx.lineWidth = 2;
      for (const road of playerStatus.nearbyRoads) {
        if (road.points.length < 2) continue;
        ctx.beginPath();
        road.points.forEach((point, index) => {
          const mapped = worldToMap(point.x, point.z);
          if (index === 0) ctx.moveTo(mapped.x, mapped.y);
          else ctx.lineTo(mapped.x, mapped.y);
        });
        ctx.stroke();
      }
    }

    const { lat, lon } = playerStatus.gpsLocation;

    ctx.font = '9px sans-serif';
    for (const city of Object.values(BW_CITIES)) {
      const dx = (city.lon - lon) * scale;
      const dy = -(city.lat - lat) * scale;
      const x = size / 2 + dx;
      const y = size / 2 + dy;

      if (x > 0 && x < size && y > 0 && y < size) {
        ctx.fillStyle = '#ffcc00';
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.fillText(city.name.substring(0, 6), x + 5, y + 3);
      }
    }

    // Spieler
    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.rotate(headingRad);

    ctx.fillStyle = '#00aaff';
    ctx.beginPath();
    ctx.moveTo(0, -10);
    ctx.lineTo(-6, 8);
    ctx.lineTo(0, 4);
    ctx.lineTo(6, 8);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    ctx.strokeStyle = 'rgba(0, 255, 255, 0.25)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, size - 2, size - 2);
  }, [playerStatus, headingRad]);

  if (!playerStatus) return null;

  const roadName = playerStatus.gpsLocation.roadName || 'Unbekannte Strasse';
  const cityName = playerStatus.gpsLocation.cityName || 'Baden-Wuerttemberg';
  const speed = Math.round(playerStatus.speed || 0);
  const limit = playerStatus.speedLimit;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 5
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 18,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'rgba(255,255,255,0.7)',
          textAlign: 'center',
          fontFamily: 'Trebuchet MS, Lucida Sans Unicode, Lucida Grande, Lucida Sans, Arial, sans-serif'
        }}
      >
        <Typography sx={{ fontSize: '0.95rem', letterSpacing: '0.08em' }}>
          {roadName}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
          {cityName}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          width: 240,
          height: 240,
          borderTopRightRadius: '100%',
          background: 'radial-gradient(circle at 20% 80%, rgba(0,255,255,0.25), rgba(0,0,0,0.6) 60%)',
          border: '1px solid rgba(0,255,255,0.35)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: 3
        }}
      >
        <Typography sx={{ color: '#00ffff', fontSize: '2.2rem', fontWeight: 700, lineHeight: 1 }}>
          {speed}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
          km/h
        </Typography>
        <Box sx={{ mt: 1 }}>
          {limit && (
            <Typography sx={{ color: playerStatus.isOverspeed ? '#ff6666' : '#66ff88', fontSize: '0.75rem' }}>
              Limit {limit} km/h
            </Typography>
          )}
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>
            Wetter: {weather?.type ?? '-'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>
            Zeit: {timeOfDay ?? '-'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          width: 240,
          height: 240,
          borderTopLeftRadius: '100%',
          background: 'radial-gradient(circle at 80% 80%, rgba(255,0,255,0.25), rgba(0,0,0,0.6) 60%)',
          border: '1px solid rgba(255,0,255,0.35)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          padding: 3,
          gap: 1
        }}
      >
        <Box
          component="canvas"
          ref={minimapRef}
          width={150}
          height={150}
          sx={{
            borderRadius: 1.25,
            boxShadow: '0 2px 12px rgba(0,0,0,0.35)'
          }}
        />
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
          Wanted: {playerStatus.wantedLevel}
        </Typography>
      </Box>
    </Box>
  );
};

export default OpenWorldDriveHUD;
